import { Badge } from "@/components/ui/badge";
import { NO_DEAD_CLICK_CLASS, PII_MASK_CLASS } from "@/lib/analytics";
import { useSupplierProduct } from "@/queries/order-assistant";
import { formatMoney } from "@/lib/order-assistant/format";
import { isFetchError, type StoreSupplierProductVariant } from "@repo/api-sdk";
import { cn } from "@repo/design-system/utils";
import { Button } from "@medusajs/ui";
import { ChevronLeft } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { CardImage, NOT_ON_SALONSTOCKER } from "./CardsView";
import { LinkButton } from "./LinkButton";
import type { SupplierDetailTarget } from "./OrderAssistantProvider";

/** The variant's own title, or just its option values (joined with " / ", no
 *  option names) when it has none, so every row reads alike; the named option
 *  values go on a second line only when the title does not already say them. */
function variantLabels(variant: StoreSupplierProductVariant): {
  primary: string;
  secondary: string | null;
} {
  const optionText = variant.option_values
    .map(({ name, value }) => `${name}: ${value}`)
    .join(" · ");
  if (!variant.variant_title) {
    return {
      primary: variant.option_values.map(({ value }) => value).join(" / "),
      secondary: null
    };
  }
  const title = variant.variant_title.toLowerCase();
  const titleHasValues = variant.option_values.every(({ value }) =>
    title.includes(value.toLowerCase())
  );
  return {
    primary: variant.variant_title,
    secondary: optionText && !titleHasValues ? optionText : null
  };
}

/**
 * The read-only supplier product view inside the drawer, opened from a
 * supplier-only card: a gallery, the description, and a variant table with
 * option values, price and trade price (only when the api sent one). The route
 * lists only public, available variants, so availability is said once under the
 * table. There is no add-to-cart and no link out to a supplier site.
 *
 * Rendered inside the drawer's dialog content, which is where focus is
 * trapped. It takes focus on mount and gives it back to the card that opened
 * it on the way out. It carries the replay mask itself rather than relying on
 * the drawer content around it.
 */
export function SupplierDetailView({
  target,
  onClose
}: {
  target: SupplierDetailTarget;
  onClose: () => void;
}) {
  const { data, isLoading, isError, error, refetch } = useSupplierProduct(
    target.supplierProductId
  );
  const [imageIndex, setImageIndex] = useState(0);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Focus moves in on open and back to whatever opened the view on close.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    return () => {
      if (opener && document.contains(opener)) opener.focus();
    };
  }, []);
  // The heading only exists once the product has loaded (or failed).
  useEffect(() => {
    if (!isLoading) headingRef.current?.focus();
  }, [isLoading]);

  const hasTrade = useMemo(
    () => data?.variants.some((variant) => variant.trade_price !== undefined),
    [data]
  );

  const notFound = isFetchError(error) && error.status === 404;
  const images = data?.images ?? [];
  const image = images[Math.min(imageIndex, Math.max(images.length - 1, 0))];

  return (
    <div
      className={cn(
        PII_MASK_CLASS,
        NO_DEAD_CLICK_CLASS,
        "flex min-h-0 flex-1 flex-col"
      )}
    >
      <div className="border-ui-border-base flex items-center gap-x-2 border-b px-2 py-2">
        <Button
          type="button"
          variant="transparent"
          size="small"
          onClick={onClose}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Back to chat
        </Button>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
        {isLoading ? (
          <p role="status" className="text-ui-fg-subtle text-sm">
            Loading product
          </p>
        ) : isError || !data ? (
          <div role="alert" className="space-y-2">
            <h2
              ref={headingRef}
              tabIndex={-1}
              className="text-ui-fg-base text-base font-semibold outline-none"
            >
              Supplier product
            </h2>
            <p className="text-ui-fg-subtle text-sm">
              {notFound
                ? "This product is no longer available."
                : "We couldn't load this product."}
            </p>
            {!notFound ? (
              <LinkButton onClick={() => void refetch()}>Try again</LinkButton>
            ) : null}
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <CardImage
                src={image ?? null}
                supplierHosted
                className="h-56 w-full"
              />
              {images.length > 1 ? (
                <ul className="flex gap-2 overflow-x-auto pb-1">
                  {images.map((url, index) => (
                    <li key={url} className="shrink-0">
                      <button
                        type="button"
                        onClick={() => setImageIndex(index)}
                        aria-label={`Image ${index + 1} of ${images.length}`}
                        aria-current={index === imageIndex ? "true" : undefined}
                        className={cn(
                          "focus-visible:shadow-borders-focus block rounded-md border focus-visible:outline-none",
                          index === imageIndex
                            ? "border-ui-border-interactive"
                            : "border-ui-border-base"
                        )}
                      >
                        <CardImage
                          src={url}
                          supplierHosted
                          className="h-14 w-14"
                        />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            <div className="space-y-1">
              <h2
                ref={headingRef}
                tabIndex={-1}
                className="text-ui-fg-base text-lg font-semibold outline-none"
              >
                {data.title}
              </h2>
              <p className="text-ui-fg-subtle text-sm">
                {[
                  data.brand,
                  data.seller_name ? `Supplier: ${data.seller_name}` : null
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              {target.notOnSalonStocker ? (
                <Badge variant="warning">{NOT_ON_SALONSTOCKER}</Badge>
              ) : null}
            </div>

            {data.options.length > 0 ? (
              <dl className="space-y-1 text-sm">
                {data.options.map((option) => (
                  <div key={option.name} className="flex gap-x-2">
                    <dt className="text-ui-fg-subtle shrink-0">
                      {option.name}
                    </dt>
                    <dd className="text-ui-fg-base">
                      {option.values.join(", ")}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : null}

            {data.description ? (
              <section className="space-y-1">
                <h3 className="text-ui-fg-base text-sm font-semibold">
                  Description
                </h3>
                <p className="text-ui-fg-subtle whitespace-pre-line break-words text-sm">
                  {data.description}
                </p>
              </section>
            ) : null}

            <section className="space-y-2">
              <h3 className="text-ui-fg-base text-sm font-semibold">
                Variants
              </h3>
              <div className="border-ui-border-base overflow-x-auto rounded-lg border">
                <table className="w-full text-left text-sm">
                  <thead className="bg-ui-bg-subtle text-ui-fg-subtle text-xs">
                    <tr>
                      <th scope="col" className="px-2 py-2 font-medium sm:px-3">
                        Variant
                      </th>
                      <th
                        scope="col"
                        className="whitespace-nowrap px-2 py-2 text-right font-medium sm:px-3"
                      >
                        Price
                      </th>
                      {hasTrade ? (
                        <th
                          scope="col"
                          className="whitespace-nowrap px-2 py-2 text-right font-medium sm:px-3"
                        >
                          Trade price
                        </th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {data.variants.map((variant) => {
                      const selected =
                        variant.supplier_variant_id ===
                        target.supplierVariantId;
                      const labels = variantLabels(variant);
                      return (
                        <tr
                          key={variant.supplier_variant_id}
                          aria-current={selected ? "true" : undefined}
                          className={cn(
                            "border-ui-border-base border-t align-top",
                            selected && "bg-ui-bg-highlight"
                          )}
                        >
                          <td className="px-2 py-2 sm:px-3">
                            <span className="text-ui-fg-base block">
                              {labels.primary || data.title}
                            </span>
                            {labels.secondary ? (
                              <span className="text-ui-fg-subtle block text-xs">
                                {labels.secondary}
                              </span>
                            ) : null}
                            {variant.sku || variant.ean ? (
                              <span className="text-ui-fg-muted block text-xs">
                                {[
                                  variant.sku ? `SKU ${variant.sku}` : null,
                                  variant.ean ? `EAN ${variant.ean}` : null
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </span>
                            ) : null}
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-right sm:px-3">
                            {variant.public_price !== null
                              ? formatMoney(
                                  variant.public_price,
                                  target.currency
                                )
                              : "–"}
                          </td>
                          {hasTrade ? (
                            <td className="whitespace-nowrap px-2 py-2 text-right font-medium sm:px-3">
                              {variant.trade_price !== undefined
                                ? formatMoney(
                                    variant.trade_price,
                                    target.currency
                                  )
                                : "–"}
                            </td>
                          ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* The route lists only public, available variants. */}
              <p className="text-ui-fg-subtle text-xs">
                All listed variants are available.
              </p>
            </section>

            <p className="text-ui-fg-muted text-xs">
              Read only. You can&apos;t add this product to your cart from here.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
